import { useState } from "react";

const BOOKING_FORM_URL = "https://readdy.ai/api/form/d79bif32hsar0k5m0lh0";

export default function BookingSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(BOOKING_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="agendar" className="py-20 md:py-28" style={{ background: "linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3">
            Reservaciones
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Agenda tu cita
            <span className="text-teal-600"> en minutos</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Elige el día y hora que más te convenga. Confirmamos tu cita en menos de 24 horas.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left info */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {/* Steps */}
            <div className="bg-white rounded-2xl p-6 flex flex-col gap-5">
              <h3 className="font-bold text-gray-900 text-lg">¿Cómo funciona?</h3>
              {[
                { icon: "ri-calendar-check-line", color: "bg-teal-500", title: "Elige tu fecha y hora", desc: "Selecciona el día y horario que mejor se adapte a ti." },
                { icon: "ri-phone-line", color: "bg-emerald-500", title: "Te confirmamos", desc: "Te contactamos en menos de 24h para confirmar tu cita." },
                { icon: "ri-home-smile-line", color: "bg-green-500", title: "¡Listo! Llegamos a limpiar", desc: "Nuestro equipo llega puntual y deja tu espacio impecable." },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center ${step.color} rounded-xl shrink-0`}>
                    <i className={`${step.icon} text-white text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{step.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Availability note */}
            <div className="bg-teal-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-time-line text-white text-lg"></i>
                </div>
                <span className="font-bold text-lg">Horarios disponibles</span>
              </div>
              <ul className="text-sm text-teal-50 flex flex-col gap-1.5">
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line"></i> Lunes a Viernes: 8:00am – 6:00pm</li>
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line"></i> Sábados: 8:00am – 4:00pm</li>
                <li className="flex items-center gap-2"><i className="ri-close-circle-line text-teal-200"></i> Domingos: No disponible</li>
              </ul>
            </div>

            {/* WhatsApp shortcut */}
            <a
              href="https://wa.me/19046087529"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-4 p-5 rounded-2xl bg-green-50 hover:bg-green-100 transition-colors cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-green-500 rounded-xl shrink-0 group-hover:scale-105 transition-transform">
                <i className="ri-whatsapp-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-0.5">¿Prefieres WhatsApp?</div>
                <div className="font-bold text-gray-900 text-sm">Escríbenos directamente</div>
                <div className="text-xs text-gray-500">Respuesta inmediata</div>
              </div>
              <i className="ri-arrow-right-line text-green-400 ml-auto"></i>
            </a>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-20 bg-white rounded-2xl">
                <div className="w-20 h-20 flex items-center justify-center bg-teal-500 rounded-full mb-5">
                  <i className="ri-calendar-check-line text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Cita solicitada!</h3>
                <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
                  Recibimos tu solicitud. Te contactaremos en menos de 24 horas para confirmar tu cita en <strong className="text-gray-700">Jacksonville, FL</strong>.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-teal-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                >
                  Agendar otra cita
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="booking-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-6 md:p-8 flex flex-col gap-5"
              >
                <h3 className="text-xl font-bold text-gray-900">Completa tu reservación</h3>

                {/* Name & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre completo *</label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      placeholder="Tu nombre"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono *</label>
                    <input
                      type="tel"
                      name="telefono"
                      required
                      placeholder="(904) 000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Correo electrónico</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="tu@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50"
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha preferida *</label>
                    <input
                      type="date"
                      name="fecha"
                      required
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Hora preferida *</label>
                    <select
                      name="hora"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50 cursor-pointer"
                    >
                      <option value="">Selecciona...</option>
                      <option value="8:00am">8:00am</option>
                      <option value="9:00am">9:00am</option>
                      <option value="10:00am">10:00am</option>
                      <option value="11:00am">11:00am</option>
                      <option value="12:00pm">12:00pm</option>
                      <option value="1:00pm">1:00pm</option>
                      <option value="2:00pm">2:00pm</option>
                      <option value="3:00pm">3:00pm</option>
                      <option value="4:00pm">4:00pm</option>
                      <option value="5:00pm">5:00pm</option>
                    </select>
                  </div>
                </div>

                {/* Service type & Frequency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de servicio *</label>
                    <select
                      name="tipo_servicio"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50 cursor-pointer"
                    >
                      <option value="">Selecciona...</option>
                      <option value="Limpieza de Casa">Limpieza de Casa</option>
                      <option value="Limpieza de Apartamento">Limpieza de Apartamento</option>
                      <option value="Limpieza de Oficina">Limpieza de Oficina</option>
                      <option value="Move In">Move In</option>
                      <option value="Move Out">Move Out</option>
                      <option value="Limpieza Profunda">Limpieza Profunda</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Frecuencia</label>
                    <select
                      name="frecuencia"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50 cursor-pointer"
                    >
                      <option value="">Selecciona...</option>
                      <option value="Semanal">Semanal</option>
                      <option value="Quincenal">Quincenal</option>
                      <option value="Mensual">Mensual</option>
                      <option value="Servicio Único">Servicio Único</option>
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Dirección del servicio *</label>
                  <input
                    type="text"
                    name="direccion"
                    required
                    placeholder="Ej: 123 Main St, Jacksonville, FL"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notas adicionales</label>
                  <textarea
                    name="notas"
                    rows={3}
                    maxLength={500}
                    placeholder="Número de habitaciones, baños, instrucciones especiales..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-gray-50 resize-none"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  ></textarea>
                  <div className="text-xs text-gray-400 text-right mt-1">{charCount}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-base"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                  ) : (
                    <><i className="ri-calendar-check-line"></i> Confirmar mi cita</>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Al enviar, aceptas que te contactemos para confirmar tu reservación. Sin cargos hasta confirmar.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
